import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { VerifyingOverlay } from '../../components/ui/VerifyingOverlay';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { InternalMemoForm } from '../../components/forms/InternalMemoForm';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import { submitMemo } from '../../lib/memoSubmissions';
import { buildMemoDocxBase64, downloadMemoDocx } from '../../lib/memoDocx';
import {
  downloadMemoPdfFromSharePoint,
  syncMemoToSharePoint,
} from '../../lib/applicationFormSettings';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useFormWorkflowPermissions } from '../../hooks/useFormWorkflowPermissions';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { isMemoBodyEmpty } from '../../lib/memoBody';
import {
  EMPTY_INTERNAL_MEMO_FORM,
  type InternalMemoFormData,
} from '../../types/applicationForm';

export type MemoNewNavigateState = {
  memoNotice?: string;
  memoError?: string;
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

async function queueMemoSharePointSync(
  submissionId: string,
  form: InternalMemoFormData,
  applicationNo: string,
): Promise<void> {
  try {
    const docxBase64 = await buildMemoDocxBase64(form, applicationNo);
    await syncMemoToSharePoint(submissionId, docxBase64, { skipPdf: true });
  } catch (err) {
    console.warn('Memo SharePoint sync queue failed:', err);
  }
}

export function InternalMemoNewPage() {
  const { basePath } = usePortal();
  const navigate = useNavigate();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const { canSubmitInternal, loading: permissionsLoading } =
    useFormWorkflowPermissions('internal-memo');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfOverlay, setPdfOverlay] = useState<string | null>(null);
  const [form, setForm] = useState<InternalMemoFormData>(() => ({
    ...EMPTY_INTERNAL_MEMO_FORM,
    date: todayIso(),
  }));
  const lastErrorToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  const listPath = basePath ? `${basePath}/internal-memo` : '/internal-memo';

  const submitterLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  useEffect(() => {
    setForm((prev) => {
      const creator = submitterLabel.trim();
      if (!creator) return prev;
      if (prev.from_line === creator && prev.issued_by === creator) return prev;
      return { ...prev, from_line: creator, issued_by: creator };
    });
  }, [submitterLabel]);

  const validate = (): string | null => {
    if (!form.to.trim()) return 'Enter To.';
    if (!(form.issued_by.trim() || submitterLabel.trim())) {
      return 'Creator could not be resolved for Issued by.';
    }
    if (!form.date.trim()) return 'Enter Date.';
    if (!form.subject.trim()) return 'Enter Subject.';
    if (isMemoBodyEmpty(form.body)) return 'Enter Body.';
    return null;
  };

  const goToSubmission = (id: string, state?: MemoNewNavigateState) => {
    navigate(`${listPath}/${id}`, { replace: true, state });
  };

  const handleSave = async (mode: 'save' | 'docx' | 'pdf') => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setPdfOverlay(null);
    try {
      const creator = submitterLabel.trim();
      const payload: InternalMemoFormData = {
        ...form,
        from_line: creator || form.from_line.trim(),
        issued_by: creator || form.issued_by.trim(),
        acknowledged_by: '',
      };
      const row = await submitMemo(
        payload,
        submitterLabel,
        authUser?.email ?? profile?.email ?? undefined,
      );
      const savedForm = row.form_data as InternalMemoFormData;

      if (mode === 'docx') {
        await downloadMemoDocx(savedForm, row.application_no);
        void queueMemoSharePointSync(row.id, savedForm, row.application_no);
        goToSubmission(row.id, { memoNotice: 'Saved. Word document downloaded.' });
      } else if (mode === 'pdf') {
        try {
          const startedAt = Date.now();
          setPdfOverlay('Converting PDF…');
          const timer = window.setInterval(() => {
            const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
            setPdfOverlay(
              elapsedSec <= 0 ? 'Converting PDF…' : `Converting PDF… (${elapsedSec}s)`,
            );
          }, 1000);
          try {
            const docxBase64 = await buildMemoDocxBase64(savedForm, row.application_no, {
              issuedByStampName: creator || undefined,
            });
            await downloadMemoPdfFromSharePoint(row.id, docxBase64, {});
            void queueMemoSharePointSync(row.id, savedForm, row.application_no);
            goToSubmission(row.id, { memoNotice: 'Saved. PDF downloaded.' });
          } finally {
            window.clearInterval(timer);
            setPdfOverlay(null);
          }
        } catch (pdfErr) {
          setPdfOverlay(null);
          await downloadMemoDocx(savedForm, row.application_no);
          console.warn('Memo PDF export failed; DOCX downloaded instead:', pdfErr);
          goToSubmission(row.id, {
            memoNotice: 'Saved. PDF export failed — Word document downloaded instead.',
            memoError: pdfErr instanceof Error ? pdfErr.message : String(pdfErr),
          });
        }
      } else {
        void queueMemoSharePointSync(row.id, savedForm, row.application_no);
        goToSubmission(row.id, { memoNotice: 'Saved. Syncing Word to SharePoint…' });
      }
    } catch (err) {
      setPdfOverlay(null);
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  if (permissionsLoading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Checking submission permissions…</p>
      </div>
    );
  }

  if (!canSubmitInternal) {
    return (
      <div className="af-applications-page af-form-page">
        <PageHeader
          title="New Internal Memorandum"
          description="You do not have permission to create this form."
          actions={
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="af-applications-page af-form-page">
      <PageHeader
        title="New Internal Memorandum"
        actions={
          <Link to={listPath} className="btn-secondary btn-sm">
            Back to list
          </Link>
        }
      />

      {pdfOverlay ? <VerifyingOverlay message={pdfOverlay} /> : null}
      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="card memo-form-card">
        <InternalMemoForm value={form} onChange={setForm} />
        <div className="memo-form__actions">
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => void handleSave('save')}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => void handleSave('docx')}
          >
            {busy ? 'Saving…' : 'Save & download DOCX'}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => void handleSave('pdf')}
          >
            {busy ? 'Saving…' : 'Save & export PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
